import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { blogInsufficientCredits } from '@/store/slices/blog';

import { getShowInProgress, setShowInProgress } from '../../../../hooks/useShowInProgress';
import {
  CREATIVES_REVEAL_KEY,
  DEVDEBUG_REVEAL_KEY,
  FTUX_QA_REVEAL_KEY,
  SUPPORT_MAILBOX_REVEAL_KEY,
  getFeatureReveal,
  setFeatureReveal,
} from '../../../../config/featureFlags';
import { useLumioArticle } from '../../lumioArticle';
import { useLumioControl } from '../../lumioControl';
import { detectArticleRequest } from './articleIntent';

import {
  lumioChatInit,
  lumioConversationHistory,
  lumioPendingProposals,
  lumioProposalDecision,
  streamLumioReply,
  PendingProposal,
} from './endpoint';
import { ChatMessage, ChatStatus } from './types';

/** Monotonic message ids (stable React keys; no external uuid dep). */
let seq = 0;
const nextId = (role: string): string => role + '-' + (seq += 1);

/** Thinking-dots beat before the article acknowledgement, so a locally answered
 *  turn does not snap into place faster than a real reply ever could. */
const ARTICLE_ACK_MS = 700;

/** The FTUX test code: FTUX - Getting Started's QA strip (the flow itself is on for every owner). */
const FTUX_QA_CODE = '777ftuxqa';

/**
 * A "777ftuxqa" turn's answer: the QA strip in this header on, or off again when it was on (FTUX_QA_REVEAL_KEY); the
 * flow stays on either way, for every owner. Refused while a support agent is signed in as the owner, flipping
 * nothing. It sends no event.
 */
const ftuxQaCodeReply = (impersonating: boolean, t: TFunction): Pick<ChatMessage, 'content' | 'slot'> => {
  if (impersonating) return { content: t('FTUX tests are off while you are signed in as this owner.') };
  const on = !getFeatureReveal(FTUX_QA_REVEAL_KEY);
  setFeatureReveal(FTUX_QA_REVEAL_KEY, on);
  return on
    ? { slot: 'win', content: t("**Jackpot!** FTUX - Getting Started's test strip is on, in this header.") }
    : { slot: 'lose', content: t("**No win this time.** FTUX - Getting Started's test strip is off again.") };
};

/** How often the active panel re-fetches pending proposals (see the poll effect).
 * Proposals change slowly, so a light poll — plus an immediate refetch when the
 * tab becomes visible — keeps the inbox near-live without any socket/SSE infra. */
const PROPOSALS_POLL_MS = 60_000;

/**
 * Per-tab cache of the welcome suggestion topics, keyed by language. The only way
 * to fetch topics is `/chat/init`, which MINTS a conversation every call, so
 * without this every page reload would mint a fresh (usually empty) conversation
 * and flood the history list. Topics are static enough to cache for the session.
 */
const TOPICS_CACHE_KEY = 'lumioWelcomeTopics';
const readCachedTopics = (lang: string): string[] | null => {
  try {
    const raw = sessionStorage.getItem(TOPICS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { lang?: string; topics?: unknown };
    return parsed.lang === lang && Array.isArray(parsed.topics) && parsed.topics.length
      ? (parsed.topics as string[])
      : null;
  } catch {
    return null;
  }
};
const writeCachedTopics = (lang: string, topics: string[]): void => {
  try {
    sessionStorage.setItem(TOPICS_CACHE_KEY, JSON.stringify({ lang, topics }));
  } catch {
    /* sessionStorage unavailable (private mode / quota): skip caching. */
  }
};

export interface UseLumioChat {
  messages: ChatMessage[];
  status: ChatStatus;
  /** thinking || streaming, drives the send/stop button swap. */
  isBusy: boolean;
  /** Append a user turn and stream the assistant reply. No-op while busy/empty. */
  send: (text: string) => void;
  /** Abort the in-flight reply (keeps whatever streamed so far). */
  stop: () => void;
  /** Clear the conversation back to the welcome state (aborts any in-flight reply). */
  reset: () => void;
  /** Re-run the last user prompt after a stop/error. */
  retryLast: () => void;
  /** Welcome-screen suggestions from `/chat/init` (no static fallback). */
  welcome: { topics: string[]; loading: boolean; error: boolean };
  /** Re-attempt the welcome `/chat/init` fetch after an error. */
  retryWelcome: () => void;
  /** Load a past conversation's thread (from `/chat/history`) into the panel and
   * make it the active conversation, so the next turn threads onto it. */
  resumeConversation: (conversationId: string) => void;
  /** A past conversation's thread is being loaded. */
  resuming: boolean;
  /** Loading a past conversation failed (show an honest error + retry). */
  resumeError: boolean;
  /** Re-attempt the last resume after a failure. */
  retryResume: () => void;
  /** Owner-inbox proposals awaiting a decision, with their action chips.
   * Empty outside owner mode. Kept in sync with each reply. */
  pendingProposals: PendingProposal[];
  /** Record the owner's click on a proposal chip (deterministic, no LLM turn):
   * removes the proposal and appends the outcome as an assistant message. */
  decideProposal: (proposalId: string, actionId: string) => void;
}

/**
 * Owns the Lumio conversation: the message list, the request lifecycle
 * (thinking → streaming → idle), and cancellation. It talks to the real
 * `streamLumioReply` transport (`./endpoint`): the first turn lazily opens a
 * conversation (`/chat/init`), and each turn POSTs to `/chat/` and types the
 * reply out word-by-word. The `conversation_id` is held here for the panel's
 * lifetime and cleared on `reset()` so "new conversation" starts fresh. `section`
 * is read live (reserved as future context for the assistant).
 *
 * `active` gates the eager welcome fetch (`/chat/init`, for the suggestion
 * topics): pass `true` only for the surface the user is actually on (the docked
 * desktop panel, or the drawer / mobile overlay once opened) so a mounted-but-
 * hidden panel never mints a throwaway conversation.
 */
export const useLumioChat = (section: string, active = true): UseLumioChat => {
  const { t, i18n } = useTranslation();
  // A support agent signed in as the owner (from MainLayout): 777ftuxqa is refused.
  const { impersonating = false } = useLumioControl();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [status, setStatus] = useState<ChatStatus>('idle');

  const abortRef = useRef<AbortController | null>(null);
  const sectionRef = useRef(section);
  sectionRef.current = section;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  // The active conversation (from /chat/init), reused across turns; cleared on reset.
  const conversationRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  // Bumped by anything that REPLACES the thread (new conversation, resuming a
  // past one). A locally-run turn captures it and only writes back into the
  // thread it started in, so an article that finishes after the owner moved on
  // does not drop a stray reply into the conversation they moved to.
  const threadRef = useRef(0);

  // Welcome-screen suggestions from `/chat/init` (topics only; the greeting stays
  // the panel's own polished copy). No static fallback: the API is the source of
  // truth, so the UI shows loading, then either the real topics or an error.
  const [welcomeTopics, setWelcomeTopics] = useState<string[]>([]);
  const [welcomeLoading, setWelcomeLoading] = useState(false);
  const [welcomeError, setWelcomeError] = useState(false);
  // Mirror the loading flag into a ref so reset() (an event handler) can tell whether
  // a welcome init is still in flight without depending on the latest render.
  const welcomeLoadingRef = useRef(welcomeLoading);
  welcomeLoadingRef.current = welcomeLoading;
  // Forward ref to the welcome refetch (loadWelcome is defined below); reset() uses it
  // to recover a welcome init it aborts mid-flight. Kept current each render so it
  // always refetches for the live language.
  const welcomeRefetchRef = useRef<() => void>(() => undefined);
  const welcomeAbortRef = useRef<AbortController | null>(null);
  // The language the welcome topics were last fetched for: drives a refetch when
  // the settings language changes, so the API always gets the current language.
  const welcomeLangRef = useRef<string | null>(null);
  // Resuming a past conversation (loading its thread). It claims the shared
  // abortRef so a send()/reset()/another resume supersedes it (and its late
  // resolve can't clobber them); its own loading/error keep the panel honest.
  const [resuming, setResuming] = useState(false);
  const [resumeError, setResumeError] = useState(false);
  const resumeIdRef = useRef<string | null>(null);
  const [pendingProposals, setPendingProposals] = useState<PendingProposal[]>([]);
  // Proposals the owner just decided locally — remembered so an in-flight poll
  // that started before the decision can't resurrect them into the inbox.
  const decidedIdsRef = useRef<Set<string>>(new Set());

  // Current settings/interface language as the app UI short code ('en', 'mx', 'bra', …). Read live
  // so each turn + the welcome fetch follow Settings.
  //
  // Sent to Lumio AS IS. Lumio is not the owner-hub backend: it keys its copy bundles on the
  // STUDIO codes (`_LOCALE_BUNDLE` in proactive_copy.py accepts en/cen/mx/cl/co/pe/ve/arg/es/
  // bra/pt/cfr/fr/za/af), so `toBackendLang` would hand it `br` and `ar`, which it does not
  // know, and it falls back to English with only a log line. `toBackendLang` stays correct for
  // everything it was written for: saved preferences, the preview `&lang=`, the configurator.
  const lang = (i18n.language || 'en').split('-')[0];

  const isBusy = status !== 'idle';

  const run = useCallback((prompt: string): void => {
    const assistantId = nextId('assistant');
    setMessages((prev) => [...prev, { id: assistantId, role: 'assistant', content: '', streaming: true }]);
    setStatus('thinking');

    const controller = new AbortController();
    abortRef.current = controller;
    let streaming = false;

    const patch = (next: Partial<ChatMessage>): void =>
      setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, ...next } : m)));

    streamLumioReply({
      prompt,
      section: sectionRef.current,
      conversationId: conversationRef.current,
      language: (i18n.language || 'en').split('-')[0],
      signal: controller.signal,
      onToken: (chunk) => {
        if (!streaming) {
          streaming = true;
          setStatus('streaming');
        }
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, content: m.content + chunk } : m)),
        );
      },
    })
      .then((result) => {
        // Ignore a settled run that is no longer the active one: reset() and a
        // newer turn both reassign abortRef, and unmount flips mountedRef. Without
        // this guard a stopped/superseded promise would re-populate the
        // conversation reset() just cleared (so the "new conversation" would
        // silently thread onto the old one), or clobber a fresh turn's UI.
        if (!mountedRef.current || abortRef.current !== controller) return;
        // Keep the conversation so the next turn threads onto it server-side.
        conversationRef.current = result.conversationId;
        patch({
          content: result.content,
          streaming: false,
          stopped: result.aborted ? true : undefined,
          error: result.error ? true : undefined,
        });
        // Sync the inbox chips: a present array (even empty, which clears them
        // after the last decision) reflects this reply; undefined means the
        // turn never reached a reply (abort before send), so leave them.
        if (result.pendingProposals !== undefined) {
          setPendingProposals(result.pendingProposals ?? []);
        }
      })
      .finally(() => {
        if (!mountedRef.current || abortRef.current !== controller) return;
        abortRef.current = null;
        setStatus('idle');
      });
  }, [i18n]);

  /**
   * "Write me an article about X" is answered by the STUDIO, not by the chat
   * backend: `writeArticle` (from the layout) has Lumio write the article and
   * create it as a draft, with the blog list showing a generating row for the
   * whole job. The turn here is the conversation around it: a beat of thinking
   * dots, an acknowledgement that keeps its caret blinking while the work runs,
   * then the finished article announced as its own turn. A failure is patched
   * onto the acknowledgement as the panel's ordinary error + Retry row, so the
   * recovery path is the one owners already know.
   */
  const { writeArticle, writing } = useLumioArticle();

  const runArticle = useCallback(
    (topic: string): void => {
      if (!writeArticle) return;
      const assistantId = nextId('assistant');
      const thread = threadRef.current;
      setMessages((prev) => [...prev, { id: assistantId, role: 'assistant', content: '', streaming: true }]);

      /** Still the same mounted panel, on the same thread this turn started in. */
      const live = (): boolean => mountedRef.current && threadRef.current === thread;

      const patch = (next: Partial<ChatMessage>): void =>
        setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, ...next } : m)));

      // Idempotent, so a job that fails before the beat is up still reads in
      // order (acknowledgement, then outcome) instead of the other way round.
      let acked = false;
      let timer = 0;
      const landAck = (): void => {
        if (acked) return;
        acked = true;
        window.clearTimeout(timer);
        patch({
          content: t('On it. I am writing an article about {{topic}}, you can watch it appear in your blog list.', {
            topic,
          }),
        });
      };
      timer = window.setTimeout(() => {
        if (live()) landAck();
      }, ARTICLE_ACK_MS);

      writeArticle(topic)
        .then((article) => {
          if (!live()) return;
          landAck();
          patch({ streaming: false });
          setMessages((prev) => [
            ...prev,
            {
              id: nextId('assistant'),
              role: 'assistant',
              content: t('**{{title}}** is ready. I saved it as a draft, so nothing is live until you publish it.', {
                title: article.title,
              }),
            },
          ]);
        })
        .catch((err: unknown) => {
          if (!live()) return;
          acked = true;
          window.clearTimeout(timer);

          // Not enough credits is an ANSWER, not an error: the layout has
          // already opened the Buy-credits sheet, and the generic error row's
          // Retry would just re-run into the same refusal. Say what happened.
          if (blogInsufficientCredits(err)) {
            patch({
              content: t("There aren't enough AI credits for this article. Top up your balance and ask me again."),
              streaming: false,
            });
            return;
          }

          // Drop the acknowledgement: the standard error row + Retry is the
          // whole message once the article did not happen.
          patch({ content: '', streaming: false, error: true });
        });
    },
    [t, writeArticle],
  );

  const send = useCallback(
    (text: string): void => {
      const trimmed = text.trim();
      if (!trimmed || abortRef.current) return;
      // Easter egg: "777" (the slot-machine jackpot) TOGGLES the `show_in_progress`
      // feature flag and answers locally with a slot-machine card, instead of
      // shipping the digits to the API. The flag is reactive, so gated sections
      // (Website Currency, Technical details, the in-progress cells on
      // Home/Pulse/Stats) appear / vanish with no reload. Win = just turned ON
      // (reels land on 777); lose = just turned OFF (reels land on mismatched fruit).
      if (trimmed === '777') {
        const next = !getShowInProgress();
        setShowInProgress(next);
        setMessages((prev) => [
          ...prev,
          { id: nextId('user'), role: 'user', content: trimmed },
          {
            id: nextId('assistant'),
            role: 'assistant',
            slot: next ? 'win' : 'lose',
            content: next
              ? '**Jackpot!** In-progress sections are now unlocked across the studio.'
              : '**No win this time.** In-progress sections are tucked away again.',
          },
        ]);
        return;
      }
      // Same easter egg for Creatives. Website Creatives and all of Marketing are generally available, so
      // "777creatives" only reveals the one part still held back: the banner-kit layer editor. It toggles
      // CREATIVES_REVEAL_KEY.
      if (trimmed === '777creatives') {
        const next = !getFeatureReveal(CREATIVES_REVEAL_KEY);
        setFeatureReveal(CREATIVES_REVEAL_KEY, next);
        setMessages((prev) => [
          ...prev,
          { id: nextId('user'), role: 'user', content: trimmed },
          {
            id: nextId('assistant'),
            role: 'assistant',
            slot: next ? 'win' : 'lose',
            content: next
              ? '**Jackpot!** The banner kit layer editor is now unlocked.'
              : '**No win this time.** The banner kit layer editor is tucked away again.',
          },
        ]);
        return;
      }
      // "777devdebug": reveals the marketing-asset composers' raw generation controls (op checkboxes,
      // scene layers and cast), which owners never see.
      if (trimmed === '777devdebug') {
        const next = !getFeatureReveal(DEVDEBUG_REVEAL_KEY);
        setFeatureReveal(DEVDEBUG_REVEAL_KEY, next);
        setMessages((prev) => [
          ...prev,
          { id: nextId('user'), role: 'user', content: trimmed },
          {
            id: nextId('assistant'),
            role: 'assistant',
            slot: next ? 'win' : 'lose',
            content: next
              ? '**Jackpot!** Debug controls are now visible in the marketing asset composers.'
              : '**No win this time.** Debug controls are hidden again.',
          },
        ]);
        return;
      }
      // "777supportmailbox": the brand's support@ mailbox in Settings → Mailbox, which operators never see.
      if (trimmed === '777supportmailbox') {
        const next = !getFeatureReveal(SUPPORT_MAILBOX_REVEAL_KEY);
        setFeatureReveal(SUPPORT_MAILBOX_REVEAL_KEY, next);
        setMessages((prev) => [
          ...prev,
          { id: nextId('user'), role: 'user', content: trimmed },
          {
            id: nextId('assistant'),
            role: 'assistant',
            slot: next ? 'win' : 'lose',
            content: next
              ? t('**Jackpot!** The support@ mailbox is now visible in Settings > Mailbox.')
              : t('**No win this time.** The support@ mailbox is hidden again.'),
          },
        ]);
        return;
      }
      // "777ftuxqa": FTUX - Getting Started's test strip, on or off (see ftuxQaCodeReply).
      if (trimmed === FTUX_QA_CODE) {
        const reply = ftuxQaCodeReply(impersonating, t);
        setMessages((prev) => [
          ...prev,
          { id: nextId('user'), role: 'user', content: trimmed },
          { id: nextId('assistant'), role: 'assistant', ...reply },
        ]);
        return;
      }
      // An article request is studio work, not a question (see ./articleIntent).
      // One at a time: a second ask while the first is still being written would
      // open a second generating row, so it gets an honest answer instead.
      const topic = writeArticle ? detectArticleRequest(trimmed) : null;
      if (topic) {
        setMessages((prev) => [...prev, { id: nextId('user'), role: 'user', content: trimmed }]);
        if (writing) {
          setMessages((prev) => [
            ...prev,
            {
              id: nextId('assistant'),
              role: 'assistant',
              content: t('I am still writing the previous article. Let me finish that one first.'),
            },
          ]);
        } else {
          runArticle(topic);
        }
        return;
      }

      setMessages((prev) => [...prev, { id: nextId('user'), role: 'user', content: trimmed }]);
      run(trimmed);
    },
    [impersonating, run, runArticle, t, writeArticle, writing],
  );

  const stop = useCallback((): void => {
    abortRef.current?.abort();
  }, []);

  const reset = useCallback((): void => {
    abortRef.current?.abort();
    abortRef.current = null;
    // New thread: a locally-run turn still in flight must not write into it.
    threadRef.current += 1;
    // Also cancel any in-flight welcome init: otherwise a late resolve would
    // re-seed conversationRef (its `if (!conversationRef.current)` sees the null
    // we just set) and silently thread the "new conversation" onto a stale,
    // pre-reset conversation.
    const welcomeWasLoading = welcomeLoadingRef.current;
    welcomeAbortRef.current?.abort();
    welcomeAbortRef.current = null;
    if (welcomeWasLoading) {
      // The aborted init never runs its .finally, so welcomeLoading would stay stuck
      // true (skeletons forever, no retry) and a ref reset alone wouldn't re-trigger
      // the active-panel effect (it doesn't depend on the ref). Drive a fresh welcome
      // fetch directly to recover. A settled/cached welcome is left untouched, so its
      // topics stay on screen with no refetch.
      welcomeRefetchRef.current();
    }
    // Drop the conversation so the next turn opens a fresh one (`/chat/init`). The
    // abort above also supersedes an in-flight resume; clear its UI state too so a
    // pending resume can't resurrect the thread we're clearing.
    conversationRef.current = null;
    setResuming(false);
    setResumeError(false);
    setMessages([]);
    // The inbox is NOT conversation state: the proposals are still pending for
    // this owner whatever happens to the thread. Clearing it here emptied the
    // tray, its badge and the Needs-you card until the next poll (up to a
    // minute) for anyone who pressed "New conversation".
    setStatus('idle');
  }, []);

  // A chip click records the decision straight through the proposals API (no
  // chat turn): drop the proposal from the inbox immediately for responsive
  // feedback, then append the outcome — including the producing service's
  // reply — as an assistant message. On failure, restore nothing new but show
  // an error turn so the owner can retry via chat.
  const decideProposal = useCallback((proposalId: string, actionId: string): void => {
    // Remember it as decided so the background poll can't re-add it if its request
    // was already in flight when the owner clicked.
    decidedIdsRef.current.add(proposalId);
    setPendingProposals((prev) => prev.filter((p) => p.proposal_id !== proposalId));
    lumioProposalDecision(proposalId, actionId, lang)
      .then((res) => {
        if (!mountedRef.current) return;
        setMessages((prev) => [
          ...prev,
          { id: nextId('assistant'), role: 'assistant', content: res.message },
        ]);
      })
      .catch(() => {
        // The decision did NOT land, so stop suppressing the proposal: the next
        // poll brings it back and the owner gets their chips again. Without this
        // the error tells them to retry while the only way to retry is gone, and
        // the proposal stays pending server-side but invisible for the session.
        decidedIdsRef.current.delete(proposalId);
        if (!mountedRef.current) return;
        setMessages((prev) => [
          ...prev,
          {
            id: nextId('assistant'),
            role: 'assistant',
            content: t('I could not record that decision. Please try again.'),
            error: true,
          },
        ]);
      });
  }, [t, lang]);

  const retryLast = useCallback((): void => {
    if (abortRef.current) return;
    const lastUser = [...messagesRef.current].reverse().find((m) => m.role === 'user');
    if (!lastUser) return;
    // Drop a trailing errored/stopped assistant turn before re-asking.
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      return last && last.role === 'assistant' && (last.error || last.stopped) ? prev.slice(0, -1) : prev;
    });
    // Retrying an article request has to re-run the article job, not ask the
    // chat backend to answer the prompt in words.
    const topic = writeArticle ? detectArticleRequest(lastUser.content) : null;
    if (topic) {
      if (!writing) runArticle(topic);
      return;
    }
    run(lastUser.content);
  }, [run, runArticle, writeArticle, writing]);

  const loadWelcome = useCallback((langCode: string): void => {
    welcomeLangRef.current = langCode;
    welcomeAbortRef.current?.abort();
    // Cache hit: render the topics with no network call, so a reload never mints a
    // throwaway conversation. The first send lazily inits its own conversation.
    const cached = readCachedTopics(langCode);
    if (cached) {
      setWelcomeError(false);
      setWelcomeLoading(false);
      setWelcomeTopics(cached);
      return;
    }
    const controller = new AbortController();
    welcomeAbortRef.current = controller;
    setWelcomeError(false);
    setWelcomeLoading(true);
    // `langCode` is the app UI code (also this fetch's cache key above); Lumio keys its copy on it.
    lumioChatInit(langCode, controller.signal)
      .then((res) => {
        if (controller.signal.aborted || !mountedRef.current) return;
        // Proposals are no longer read from init: the dedicated /proposals/pending
        // poll (see the effect below) is the source of truth, so a topics cache-hit
        // that skips init entirely can't leave the inbox empty. The /chat/ reply
        // still refines them per turn.
        const topics = Array.isArray(res.suggested_topics) ? res.suggested_topics : [];
        // A successful-but-empty response is a degraded API, not a valid "no
        // suggestions" state: surface the honest error + retry (never bare,
        // dead-end section headers).
        if (topics.length === 0) {
          welcomeLangRef.current = null; // let a retry / language change refetch
          setWelcomeError(true);
          return;
        }
        setWelcomeTopics(topics);
        writeCachedTopics(langCode, topics);
        // Reuse this conversation for the first turn, unless one already started.
        if (!conversationRef.current) conversationRef.current = res.conversation_id;
      })
      .catch(() => {
        if (controller.signal.aborted || !mountedRef.current) return;
        welcomeLangRef.current = null; // let the user retry
        setWelcomeError(true);
      })
      .finally(() => {
        if (controller.signal.aborted || !mountedRef.current) return;
        setWelcomeLoading(false);
      });
  }, []);
  // Keep the forward ref pointed at the current-language refetch for reset()'s recovery.
  welcomeRefetchRef.current = () => loadWelcome(lang);

  // Fetch suggestions when this panel becomes the active surface (never for a
  // mounted-but-hidden one), and refetch when the settings language changes so the
  // API always reflects the current language.
  useEffect(() => {
    if (!active || welcomeLangRef.current === lang) return;
    loadWelcome(lang);
  }, [active, lang, loadWelcome]);

  // Pending proposals, fetched independently of chat so the inbox/badge populate
  // on open and stay fresh as proposals are created or decided. Only the ACTIVE
  // surface polls (never a hidden-but-mounted one): it loads immediately, then
  // every PROPOSALS_POLL_MS, and again the moment the tab becomes visible — while
  // skipping ticks on a hidden tab so a backgrounded panel is silent. A blip keeps
  // whatever the inbox already shows (never clears on error); a just-decided
  // proposal is filtered out so an in-flight poll can't resurrect it.
  useEffect(() => {
    if (!active) return undefined;
    const controller = new AbortController();
    const load = (): void => {
      lumioPendingProposals(lang, controller.signal)
        .then((list) => {
          if (controller.signal.aborted || !mountedRef.current) return;
          setPendingProposals(list.filter((p) => !decidedIdsRef.current.has(p.proposal_id)));
        })
        .catch(() => {
          /* best-effort: a fetch blip keeps whatever the inbox already shows */
        });
    };
    load();
    const interval = window.setInterval(() => {
      if (typeof document === 'undefined' || !document.hidden) load();
    }, PROPOSALS_POLL_MS);
    const onVisible = (): void => {
      if (!document.hidden) load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      controller.abort();
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // `lang` is a dep so switching UI language re-fetches proposals localized.
  }, [active, lang]);

  const retryWelcome = useCallback((): void => {
    loadWelcome(lang);
  }, [lang, loadWelcome]);

  const resumeConversation = useCallback((conversationId: string): void => {
    // Claim the shared controller (aborting any in-flight reply OR resume) so a
    // later send()/reset()/resume supersedes us and our late resolve can't clobber
    // them. Status -> idle synchronously so an interrupted stream can't leave the
    // composer stuck on Stop.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    resumeIdRef.current = conversationId;
    // Another thread replacement, same reasoning as reset().
    threadRef.current += 1;
    setStatus('idle');
    setResumeError(false);
    setResuming(true);
    lumioConversationHistory(conversationId, controller.signal)
      .then((history) => {
        if (!mountedRef.current || abortRef.current !== controller) return;
        conversationRef.current = conversationId;
        setMessages(
          history.map(
            (m): ChatMessage => ({ id: m.id, role: m.type === 'human' ? 'user' : 'assistant', content: m.content }),
          ),
        );
      })
      .catch(() => {
        // A supersede/abort is expected; a genuine failure must show honestly.
        if (!mountedRef.current || abortRef.current !== controller || controller.signal.aborted) return;
        setResumeError(true);
      })
      .finally(() => {
        if (!mountedRef.current || abortRef.current !== controller) return;
        abortRef.current = null;
        setResuming(false);
      });
  }, []);

  const retryResume = useCallback((): void => {
    if (resumeIdRef.current) resumeConversation(resumeIdRef.current);
  }, [resumeConversation]);

  // Abort in-flight requests if the panel unmounts (and stop the settled promises
  // from touching state on a gone component). Re-arm mountedRef on (re)mount so the
  // guards stay correct if the instance is ever remounted (e.g. StrictMode).
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
      welcomeAbortRef.current?.abort();
    };
  }, []);

  return {
    messages,
    status,
    isBusy,
    send,
    stop,
    reset,
    retryLast,
    resumeConversation,
    resuming,
    resumeError,
    retryResume,
    welcome: { topics: welcomeTopics, loading: welcomeLoading, error: welcomeError },
    retryWelcome,
    pendingProposals,
    decideProposal,
  };
};
